import { createContext, useContext } from "react";
import { useTranslations } from "next-intl";
import { IconBolt } from "@tabler/icons-react";
import { useDelayedTooltip } from "@/hooks/useDelayedTooltip";
import { OverwriteEntry } from "@/lib/utils/workflow-overwrite-preview";

/**
 * Phase B of the workflow-overwrite preview: live, per-field inline badges in
 * the guest detail popup. Phase A warns once at save time (a dialog); this puts
 * a small marker right next to every field an enabled automation (UPDATE_GUEST)
 * will change, so the admin SEES — while editing — which of their inputs an
 * automation is about to overwrite.
 *
 * Wiring is via context (keyed by canonical field name) so the shared leaf
 * input ({@link DataTypeInput}) renders a badge without every intermediate
 * field-group component having to drill the preview through. Outside the guest
 * popup there's no provider → {@link WorkflowFieldBadge} renders nothing.
 */
const WorkflowOverwriteContext =
  createContext<Map<string, OverwriteEntry> | null>(null);

export const WorkflowOverwriteProvider = WorkflowOverwriteContext.Provider;

/** Lookup the predicted overwrite for a given canonical field name (or none). */
export function useWorkflowOverwrite(
  fieldName: string | undefined,
): OverwriteEntry | undefined {
  const map = useContext(WorkflowOverwriteContext);
  if (!map || !fieldName) return undefined;
  return map.get(fieldName);
}

/**
 * Small icon shown next to a field's label when an automation will write it.
 * Red when it conflicts with (overwrites) the admin's current value, muted
 * otherwise. Hover reveals which automation(s) and the from→to change.
 */
export const WorkflowFieldBadge: React.FC<{ fieldName?: string }> = ({
  fieldName,
}) => {
  const entry = useWorkflowOverwrite(fieldName);
  const t = useTranslations("popup-create-guest");

  // Hooks must run unconditionally — build the message first, gate render after.
  const empty = t("workflowPreviewEmpty");
  let message = "";
  if (entry) {
    if (entry.ambiguous) {
      message = t("workflowPreviewAmbiguous");
    } else if (entry.isConflict) {
      message = t("workflowBadgeConflict", {
        from: entry.fromValueDisplay || empty,
        to: entry.toValueDisplay || empty,
      });
    } else {
      message = t("workflowBadgeSet", { to: entry.toValueDisplay || empty });
    }
    if (entry.workflowNames.length > 0) {
      message += " " + t("workflowPreviewVia", { names: entry.workflowNames.join(", ") });
    }
  }

  const { ref, onMouseEnter, onMouseLeave, tooltip } =
    useDelayedTooltip<HTMLSpanElement>(message);

  if (!entry) return null;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="shrink-0 cursor-help inline-flex"
        aria-label={message}
      >
        <IconBolt
          size={14}
          className={entry.isConflict || entry.ambiguous ? "font-error" : "font-tertiary"}
        />
      </span>
      {tooltip}
    </>
  );
};
